/**
 * WingsSpendModal - Modal for spending Wings on route optimization
 *
 * Options:
 * 1. Just create 5 new lucrative routes
 * 2. Replace stale routes AND create 5 new lucrative routes
 */

import React, { useMemo, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useWorldState } from "../state/WorldStateContext.jsx";
import { debug } from '../../lib/debug.js';
import {
  getWingsState,
  findStaleRoutes,
  spendWingOnRoutes,
  WINGS_CONFIG
} from "../../systems/WingsSystem.js";
import { GLOBAL_AIRPORTS } from "../../data/GlobalAirports.js";
import { Button } from "@/components/ui/button";

export default function WingsSpendModal({ isOpen, onClose }) {
  const { t } = useTranslation();
  const { worldState, setWorldState } = useWorldState();

  const wings = getWingsState(worldState);
  const staleRoutes = useMemo(() => findStaleRoutes(worldState), [worldState]);
  const canAfford = (wings.balance || 0) >= WINGS_CONFIG.routeGenerationCost;

  // Multi-hub support: let player choose which hub to generate routes from
  // Use ownedHubs (canonical source) with airline.hubs as fallback
  const playerHubs = useMemo(() => {
    const hubs = worldState?.ownedHubs || worldState?.airline?.hubs || [];
    const primaryHub = worldState?.airline?.hub || 'KPDX';
    // Ensure primary hub is in the list, deduplicate
    const combined = hubs.includes(primaryHub) ? hubs : [primaryHub, ...hubs];
    return [...new Set(combined.length > 0 ? combined : [primaryHub])];
  }, [worldState?.ownedHubs, worldState?.airline?.hubs, worldState?.airline?.hub]);

  const [selectedHub, setSelectedHub] = useState(playerHubs[0]);

  const handleSpend = (mode) => {
    const result = spendWingOnRoutes(worldState, setWorldState, GLOBAL_AIRPORTS, mode, selectedHub);

    if (result.success) {
      debug('[Wings] Routes generated:', result.newRoutes.length);
      if (result.removedRoutes?.length > 0) {
        debug('[Wings] Stale routes removed:', result.removedRoutes.length);
      }
      onClose();
    } else {
      debug('[Wings] Failed:', result.error);
      alert(result.error);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div
        className="rounded-lg max-w-md w-full mx-4"
        style={{ backgroundColor: "#1a0f2e", border: "2px solid #8b5cf6" }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-4 border-b"
          style={{ borderColor: "#8b5cf640" }}
        >
          <div className="flex items-center gap-3">
            <span style={{ fontSize: "24px" }}>{t('wingsModal.title')}</span>
            <span
              className="text-2xl font-bold font-mono"
              style={{ color: "#a78bfa" }}
            >
              {wings.balance || 0}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{ color: "#8b5cf6", fontWeight: "bold", fontSize: "18px" }}
          >
            X
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          <p style={{ color: "#a1a1a1", fontSize: "14px" }}>
            <Trans i18nKey="wingsModal.spendDescription">
              Spend <span style={{ color: "#a78bfa", fontWeight: "bold" }}>1 Wing</span> to
              auto-generate <span style={{ color: "#22c55e", fontWeight: "bold" }}>5 new routes</span> to
              the closest airports you don't have routes to yet.
            </Trans>
          </p>

          {/* Hub selector - only show if player has multiple hubs */}
          {playerHubs.length > 1 && (
            <div
              className="p-3 rounded-lg"
              style={{ backgroundColor: "#0f0a1a", border: "1px solid #8b5cf640" }}
            >
              <div className="text-xs font-bold mb-2" style={{ color: "#a78bfa" }}>
                Generate routes from:
              </div>
              <div className="flex flex-wrap gap-2">
                {playerHubs.map(hub => {
                  const airport = GLOBAL_AIRPORTS[hub];
                  const isSelected = selectedHub === hub;
                  return (
                    <button
                      key={hub}
                      onClick={() => setSelectedHub(hub)}
                      className="px-3 py-1.5 rounded text-sm transition-all"
                      style={{
                        backgroundColor: isSelected ? "#8b5cf630" : "#1a1a1a",
                        border: isSelected ? "1px solid #8b5cf6" : "1px solid #333",
                        color: isSelected ? "#a78bfa" : "#737373",
                      }}
                    >
                      <span className="font-mono font-bold">{hub}</span>
                      {airport?.city && (
                        <span className="ml-1 opacity-60">
                          {airport.city.split(',')[0]}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Option 1: Just new routes */}
          <button
            onClick={() => handleSpend('new')}
            disabled={!canAfford}
            className="w-full p-4 rounded-lg text-left transition-all"
            style={{
              backgroundColor: canAfford ? "#1a2e1a" : "#1a1a1a",
              border: canAfford ? "1px solid #22c55e60" : "1px solid #333",
              opacity: canAfford ? 1 : 0.5,
              cursor: canAfford ? "pointer" : "not-allowed",
            }}
          >
            <div className="font-bold mb-1" style={{ color: canAfford ? "#22c55e" : "#666" }}>
              {t('wingsModal.createNew')}
            </div>
            <div style={{ color: "#737373", fontSize: "12px" }}>
              {t('wingsModal.createNewDesc')}
            </div>
          </button>

          {/* Option 2: Replace stale + new routes */}
          <button
            onClick={() => handleSpend('replace')}
            disabled={!canAfford}
            className="w-full p-4 rounded-lg text-left transition-all"
            style={{
              backgroundColor: canAfford ? "#2e1a1a" : "#1a1a1a",
              border: canAfford ? "1px solid #f59e0b60" : "1px solid #333",
              opacity: canAfford ? 1 : 0.5,
              cursor: canAfford ? "pointer" : "not-allowed",
            }}
          >
            <div className="font-bold mb-1" style={{ color: canAfford ? "#f59e0b" : "#666" }}>
              {t('wingsModal.replaceStale')}
            </div>
            <div style={{ color: "#737373", fontSize: "12px" }}>
              {staleRoutes.length > 0 ? (
                t(staleRoutes.length === 1 ? 'wingsModal.replaceStaleDesc' : 'wingsModal.replaceStaleDescPlural', { count: staleRoutes.length })
              ) : (
                t('wingsModal.noStaleFound')
              )}
            </div>
          </button>

          {/* Stale routes preview */}
          {staleRoutes.length > 0 && (
            <div
              className="p-3 rounded"
              style={{ backgroundColor: "#1a1a1a", border: "1px solid #333" }}
            >
              <div className="text-xs font-bold mb-2" style={{ color: "#ef4444" }}>
                {t('wingsModal.staleRoutesWarning')}
              </div>
              <div className="space-y-1">
                {staleRoutes.slice(0, 5).map(route => (
                  <div key={route.id} className="text-xs" style={{ color: "#737373" }}>
                    {route.origin} - {route.destination}
                  </div>
                ))}
                {staleRoutes.length > 5 && (
                  <div className="text-xs" style={{ color: "#525252" }}>
                    {t('wingsModal.andMore', { count: staleRoutes.length - 5 })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Not enough Wings warning */}
          {!canAfford && (
            <div
              className="p-3 rounded text-center"
              style={{ backgroundColor: "#2a1a1a", border: "1px solid #ef444440" }}
            >
              <span style={{ color: "#ef4444", fontSize: "13px" }}>
                {t('wingsModal.notEnoughWings', { cost: WINGS_CONFIG.routeGenerationCost })}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="p-4 border-t flex justify-end"
          style={{ borderColor: "#8b5cf640" }}
        >
          <Button
            variant="ghost"
            onClick={onClose}
            style={{ color: "#737373" }}
          >
            {t('wingsModal.cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
}
